/**
 * QuizFlashcardEditorModal — reusable preview + edit dialog for the
 * 4-option multiple-choice flashcards attached to a lesson kit or
 * educational video.
 *
 * The host owns persistence: it supplies `onSave(questions)`, which is
 * called with the cleaned question list once the teacher is happy.
 * Both the Lesson-Kit copilot and the Create-Video flow use this so a
 * teacher who switches between them sees the same editor.
 */
import React, { useState } from 'react';

const normaliseQuestion = (q) => {
  const options = [...(q?.options || [])];
  while (options.length < 4) options.push('');
  return {
    question_text: q?.question_text || '',
    options: options.slice(0, 4),
    correct_answer_index: Math.min(
      Math.max(Number(q?.correct_answer_index) || 0, 0),
      3,
    ),
    explanation: q?.explanation || '',
    // AI-generated diagram, if any. Not editable here — just carried
    // through untouched so editing a question's text/options doesn't
    // silently delete its diagram.
    diagram: q?.diagram || null,
  };
};

const EMPTY_QUESTION = {
  question_text: '',
  options: ['', '', '', ''],
  correct_answer_index: 0,
  explanation: '',
};

export default function QuizFlashcardEditorModal({
  initialQuestions = [],
  title = 'Edit quiz flashcards',
  onSave,
  onClose,
  saveLabel = 'Save quiz',
}) {
  const [questions, setQuestions] = useState(
    (initialQuestions || []).map(normaliseQuestion),
  );
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const update = (idx, patch) => {
    setQuestions((prev) => prev.map((q, i) => (i === idx ? { ...q, ...patch } : q)));
  };
  const updateOption = (qi, oi, value) => {
    setQuestions((prev) => prev.map((q, i) => {
      if (i !== qi) return q;
      const options = q.options.map((o, j) => (j === oi ? value : o));
      return { ...q, options };
    }));
  };
  const addQuestion = () => {
    setQuestions((prev) => [...prev, { ...EMPTY_QUESTION, options: ['', '', '', ''] }]);
  };
  const removeQuestion = (idx) => {
    setQuestions((prev) => prev.filter((_, i) => i !== idx));
  };

  const valid = questions.length > 0
    && questions.every((q) => q.question_text.trim() && q.options.every((o) => String(o).trim()));

  const save = async () => {
    if (!valid) {
      setErr('Each question needs text and all four options filled in.');
      return;
    }
    setSaving(true);
    setErr(null);
    try {
      const payload = questions.map((q) => ({
        question_text: q.question_text.trim(),
        options: q.options.map((o) => String(o).trim()),
        correct_answer_index: q.correct_answer_index,
        explanation: q.explanation?.trim() || null,
        diagram: q.diagram || null,
      }));
      await onSave(payload);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || 'Could not save the quiz');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center bg-black/50 p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl my-6">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h3 className="text-lg font-bold text-gray-900">{title}</h3>
          <button onClick={onClose} aria-label="Close" className="text-gray-500 hover:text-gray-700 text-2xl sm:text-3xl font-bold w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center rounded-full hover:bg-gray-200 transition-all"><svg className="w-5 h-5 sm:w-6 sm:h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg></button>
        </div>
        <div className="p-5 space-y-4 max-h-[65vh] overflow-y-auto">
          {questions.length === 0 && (
            <div className="text-sm text-gray-500">No questions yet — add one below.</div>
          )}
          {questions.map((q, qi) => (
            <div key={qi} className="border border-gray-200 rounded-xl p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-gray-500 uppercase flex items-center gap-2">
                  Card {qi + 1}
                  {q.diagram?.code && (
                    <span
                      className="normal-case font-medium text-purple-600 bg-purple-50 rounded-full px-2 py-0.5"
                      title="This card has a diagram — it isn't editable here, but stays attached when you save."
                    >
                      🖼️ Has diagram
                    </span>
                  )}
                </span>
                <button
                  type="button"
                  onClick={() => removeQuestion(qi)}
                  className="text-xs text-red-500 hover:text-red-700"
                >
                  Remove
                </button>
              </div>
              <textarea
                rows={2}
                value={q.question_text}
                onChange={(e) => update(qi, { question_text: e.target.value })}
                placeholder="Question"
                maxLength={600}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400 resize-none"
              />
              <div className="space-y-2">
                {q.options.map((opt, oi) => (
                  <label key={oi} className="flex items-center gap-2">
                    <input
                      type="radio"
                      name={`correct-${qi}`}
                      checked={q.correct_answer_index === oi}
                      onChange={() => update(qi, { correct_answer_index: oi })}
                      title="Mark as correct answer"
                      className="text-emerald-600"
                    />
                    <span className="text-xs font-semibold text-gray-500 w-4">{String.fromCharCode(65 + oi)}.</span>
                    <input
                      value={opt}
                      onChange={(e) => updateOption(qi, oi, e.target.value)}
                      placeholder={`Option ${String.fromCharCode(65 + oi)}`}
                      maxLength={300}
                      className={`flex-1 border rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 ${
                        q.correct_answer_index === oi
                          ? 'border-emerald-300 focus:ring-emerald-400 bg-emerald-50'
                          : 'border-gray-300 focus:ring-purple-400'
                      }`}
                    />
                  </label>
                ))}
                <p className="text-[11px] text-gray-400 pl-6">Select the radio next to the correct option.</p>
              </div>
              <textarea
                rows={3}
                value={q.explanation}
                onChange={(e) => update(qi, { explanation: e.target.value })}
                placeholder="Explanation — teach the concept in a few friendly sentences, not just why the answer is right (optional)"
                maxLength={1000}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400 resize-none"
              />
            </div>
          ))}
          <button
            type="button"
            onClick={addQuestion}
            className="text-sm font-semibold text-purple-600 hover:text-purple-800"
          >
            + Add question
          </button>
          {err && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{err}</div>
          )}
        </div>
        <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-200">
          <button onClick={onClose} className="text-sm px-4 py-2 rounded-lg border border-gray-300 text-gray-700">
            Cancel
          </button>
          <button
            onClick={save}
            disabled={saving || !valid}
            className="text-sm px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-semibold disabled:opacity-50"
          >
            {saving ? 'Saving…' : saveLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
